"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ClipboardCheck, Compass, Hammer, ShieldCheck, ArrowRight, Download, CalendarDays, FileCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface ProcessStep {
  step: string;
  iconName: "compass" | "clipboard" | "hammer" | "shield";
  title: string;
  description: string;
  deliverables: string;
  timing: string;
}
const steps: ProcessStep[] = [{
  step: "01",
  iconName: "compass",
  title: "Consultation & Vision",
  description: "We begin on-site in your home to evaluate spatial feasibility, discuss structural goals, and align realistic investment tiers with Brookhaven zoning parameters.",
  deliverables: "In-depth site walk, initial budget framework, and zoning feasibility notes.",
  timing: "Week 1"
}, {
  step: "02",
  iconName: "clipboard",
  title: "Design, Architecture & Permitting",
  description: "Our team translates your vision into millimeter-precise 3D architectural schematics, finishes, trade pricing schedules, and municipal permit submittals.",
  deliverables: "Material sample review, finalized 3D renders, and a defined scope contract.",
  timing: "Weeks 2–4"
}, {
  step: "03",
  iconName: "hammer",
  title: "Precision Construction",
  description: "An on-site project coordinator manages every trade daily, enforcing floor protection, sealed dust containment barriers, and weekly progress walkthroughs.",
  deliverables: "Weekly milestone updates, on-site project coordinator, and dust barrier mitigation.",
  timing: "Weeks 5–16"
}, {
  step: "04",
  iconName: "shield",
  title: "Final Walkthrough & Warranty",
  description: "Prior to handover, we conduct comprehensive punch list inspections, detailed post-construction sanitization, and register your multi-year craftsmanship warranty.",
  deliverables: "Complete system orientation, zero-item punch list sign-off, and warranty documentation.",
  timing: "Completion"
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-process" data-nav="light" className="relative bg-background py-24 md:py-32 text-foreground">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Header Group */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-5 rounded-full border-primary/40 bg-primary/10 px-4 py-1.5 text-xs font-semibold tracking-widest text-primary uppercase">
            The Brookhaven Standard
          </Badge>

          <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-tight">
            A disciplined, four-stage approach to elevated residential remodeling.
          </h2>

          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear milestones, detailed trade coordination, and local Brookhaven permitting experience guide every phase of your project from initial concept to final walkthrough.
          </p>

          <div className="mx-auto mt-8 flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-primary/40" />
            <div className="size-1.5 rounded-full bg-primary" />
            <div className="h-px w-12 bg-primary/40" />
          </div>
        </div>

        {/* Process Stages Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((item, i) => <motion.div key={item.step} data-index={i} variants={cardVariants} whileHover={shouldReduceMotion ? {} : {
          y: -4
        }} transition={{
          duration: 0.25,
          ease: "easeOut"
        }} className="group flex h-full">
              <Card className="flex w-full flex-col justify-between rounded-2xl border border-border/80 bg-card/80 p-6 sm:p-7 backdrop-blur-md shadow-sm transition-[box-shadow,border-color] duration-300 hover:border-primary/40 hover:shadow-md">
                <div>
                  {/* Top Metadata Row */}
                  <div className="flex items-center justify-between border-b border-border/60 pb-4">
                    <span className="font-serif text-2xl font-bold text-primary">
                      {item.step}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      <CalendarDays className="size-3.5 text-primary" />
                      {item.timing}
                    </span>
                  </div>

                  {/* Icon & Title */}
                  <div className="mt-5 flex items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-secondary">
                      {item.iconName === "compass" && <Compass className="size-5 text-primary" />}
                      {item.iconName === "clipboard" && <FileCheck className="size-5 text-primary" />}
                      {item.iconName === "hammer" && <Hammer className="size-5 text-primary" />}
                      {item.iconName === "shield" && <ShieldCheck className="size-5 text-primary" />}
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </div>

                {/* Highlight Expectation Box */}
                <div className="mt-6 rounded-xl border border-border/70 bg-muted/60 p-4">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground uppercase tracking-wider">
                    <ClipboardCheck className="size-3.5 text-primary" />
                    <span>What to expect</span>
                  </div>
                  <p className="mt-2 text-xs leading-normal text-muted-foreground">
                    {item.deliverables}
                  </p>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Consultation Callout Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-16 sm:mt-20 overflow-hidden rounded-2xl bg-secondary text-secondary-foreground shadow-xl border border-secondary">
          <div className="relative p-8 sm:p-10 lg:p-12">
            {/* Subtle architectural gold accent line */}
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />

            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <Badge variant="outline" className="mb-3 rounded-full border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary tracking-wide">
                  Predictable Scheduling & Budget Transparency
                </Badge>
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-secondary-foreground">
                  Ready to map out your Brookhaven renovation?
                </h3>
                <p className="mt-3 text-sm sm:text-base text-secondary-foreground/80 leading-relaxed max-w-xl">
                  Early architectural planning prevents costly mid-build revisions. Schedule a comprehensive consultation with our principal builder and lead designer to establish your feasibility framework.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 lg:col-span-5 lg:justify-end">
                <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 shadow-sm transition-[color,background-color,box-shadow]">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center justify-center gap-2">
                    Schedule Your Design Consultation
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 text-secondary-foreground bg-transparent hover:bg-secondary-foreground/10 hover:text-secondary-foreground transition-colors">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center justify-center gap-2">
                    <Download className="size-4 text-primary" />
                    Download Process Guide
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Full Process Link */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-10 sm:mt-12 flex justify-center">
          <Button asChild variant="outline" size="lg" className="rounded-lg border-border/80 bg-card/80 text-foreground shadow-xs hover:border-primary/50 hover:bg-muted/80 hover:text-primary transition-[color,background-color,border-color,box-shadow]">
            <Link to="#" className="inline-flex items-center justify-center gap-2">
              <span>See our full process</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}